import { MessagesSquare } from 'lucide-react'
import { EmptyState } from '../../components/ui/empty-state'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { myThreadsQuery } from '../../lib/threads'
import { myChannelsQuery } from '../../lib/channels'
import { usersQuery } from '../../lib/users'
import { Avatar } from '../../components/avatar'
import { Markdown } from '../../components/messages/markdown'

// DIS-55: threads the caller follows, newest reply first, with unread badges.

export const Route = createFileRoute('/_app/threads')({
  component: ThreadsPage,
})

function ThreadsPage() {
  const threads = useQuery({ ...myThreadsQuery, staleTime: 0 })
  const users = useQuery(usersQuery)
  const channels = useQuery(myChannelsQuery)
  const byId = new Map((users.data ?? []).map((u) => [u.id, u]))
  const chan = new Map((channels.data ?? []).map((c) => [c.id, c]))
  return (
    <>
      <header className="flex h-12 items-center gap-3 border-b border-border px-4" data-testid="channel-header">
        <h1 className="font-semibold">Threads</h1>
        <span className="text-sm text-fg-muted">Conversations you follow.</span>
      </header>
      <div className="flex-1 overflow-y-auto p-4" data-testid="threads-list">
        {threads.data?.length === 0 && <EmptyState icon={<MessagesSquare size={16} />} title="No threads yet" hint="Reply to a message, or follow a thread, and it shows up here with its unread count." />}
        <ul className="mx-auto max-w-3xl space-y-3">
          {threads.data?.map((t) => {
            const author = t.root.author_id ? byId.get(t.root.author_id) : undefined
            const c = chan.get(t.root.channel_id)
            const to = c?.slug ? { to: '/c/$slug/$messageId' as const, params: { slug: c.slug, messageId: t.root.id } } : { to: '/c/$slug/$messageId' as const, params: { slug: t.root.channel_id, messageId: t.root.id } }
            return (
              <li key={t.root.id} className={`rounded-md border p-3 text-sm ${t.unread ? 'border-accent/60' : 'border-border'}`} data-testid="thread-entry" data-unread={t.unread}>
                <div className="mb-1 flex items-center gap-2 text-xs text-fg-muted">
                  {author && <Avatar user={author} size={18} />}
                  <span className="font-medium text-fg">{author?.display_name ?? 'Unknown'}</span>
                  {c && <span>in {c.name ? `#${c.name}` : 'a direct message'}</span>}
                  <Link {...to} search={{ thread: t.root.id }} className="ml-auto hover:text-fg">
                    {t.thread.reply_count} {t.thread.reply_count === 1 ? 'reply' : 'replies'} · {new Date(t.thread.last_reply_at).toLocaleString()}
                  </Link>
                  {t.unread > 0 && (
                    <span className="rounded-full bg-accent px-1.5 text-[10px] font-semibold text-accent-fg" data-testid="thread-unread">
                      {t.unread} new
                    </span>
                  )}
                </div>
                <Markdown source={t.root.body} />
              </li>
            )
          })}
        </ul>
      </div>
    </>
  )
}
